import { useQuery } from '@apollo/client';
import { DialogTitle } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { GET_PATIENT } from '@retainerclub/shared-api';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import {
  CheckboxInput,
  DateInput,
  Fieldset,
  Input,
  LoadingOrError,
  Modal,
  TextArea,
} from '../../../../shared';
import CaseSettingsFormFields from '../../../CaseSettings/CaseSettingsFormFields';
import {
  ReferralsFormPatientScanReady,
  ReferralsFormScanOptions,
} from '../../ReferralsForm';
import ReferralsProductionListModalFormInner from './ReferralsProductionListModalFormInner';

function ReferralsProductionListModalForm({
  handleCloseModal,
  currentRecord = null,
  open,
}) {
  const [currentPatient, setCurrentPatient] = useState(null);
  const [showSuccessView, setShowSuccessView] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowSuccessView(false);
    }
  }, [open]);

  const { error: patientError, loading: patientLoading } = useQuery(
    GET_PATIENT,
    {
      variables: {
        id: currentRecord?.id,
      },
      skip: !(currentRecord?.id && open),
      onCompleted: (data) => {
        setCurrentPatient(data?.patient);
      },
    },
  );

  const shouldRender = LoadingOrError({
    loading: patientLoading,
    error: patientError,
    data: currentPatient,
    title: 'Patient',
  });

  if (shouldRender === true) {
    return (
      <Modal open={open} setOpen={handleCloseModal} size="6xl">
        {currentPatient ? (
          <>
            <div>
              <div
                className={
                  showSuccessView
                    ? 'flex items-center justify-center gap-8 text-center'
                    : 'text-center'
                }
              >
                {showSuccessView && (
                  <CheckCircleIcon
                    className="size-10 shrink-0 text-green-500"
                    aria-hidden="true"
                  />
                )}
                <DialogTitle
                  as="h2"
                  className="font-semibold text-2xl text-gray-900 leading-6"
                >
                  {showSuccessView ? (
                    <>
                      Case details for{' '}
                      <span className="capitalize">
                        {currentPatient?.firstName} {currentPatient?.lastName}
                      </span>{' '}
                      successfully updated.
                    </>
                  ) : (
                    <>
                      Update case details for&nbsp;
                      <span className="capitalize">
                        {currentPatient?.firstName} {currentPatient?.lastName}
                      </span>
                    </>
                  )}
                </DialogTitle>
              </div>
            </div>
            <div className={showSuccessView ? 'mt-6' : 'mt-4'}>
              {currentPatient && (
                <ReferralsProductionListModalFormInner
                  record={currentPatient}
                  open={open}
                  handleCloseModal={handleCloseModal}
                  onUpdateSuccess={() => setShowSuccessView(true)}
                >
                  <Fieldset
                    title="Patient Info"
                    description="Who is receiving treatment?"
                    doubleCols
                  >
                    <Input
                      className="col-span-full sm:col-span-6 xl:col-span-4"
                      required
                      name="firstName"
                      label="Patient First Name"
                    />
                    <Input
                      className="col-span-full sm:col-span-6 xl:col-span-4"
                      required
                      name="lastName"
                      label="Patient Last Name"
                    />
                    <DateInput
                      dayName="birthDay"
                      monthName="birthMonth"
                      yearName="birthYear"
                      required
                      wrapperClass="col-span-full sm:col-span-6 xl:col-span-4"
                    />
                  </Fieldset>
                  <Fieldset
                    title="Scan Details"
                    description="What is the status of the patient's scan?"
                  >
                    <div className="col-span-full mt-2 sm:col-span-4">
                      <ReferralsFormScanOptions
                        name="scanReady"
                        usePatientAttributes={false}
                      />
                    </div>
                    <div className="col-span-full mt-2 grid grid-cols-1 sm:col-span-2 sm:grid-cols-2">
                      <ReferralsFormPatientScanReady
                        usePatientAttributes={false}
                        scanReadyAt="scanReadyAt"
                        partnerUploadScanAt="partnerUploadScanAt"
                      />
                    </div>
                  </Fieldset>

                  <div className="col-span-full mt-4">
                    <CaseSettingsFormFields />
                  </div>
                  <div className="col-span-full">
                    <TextArea name="notes" label="Additional Notes" />
                  </div>
                  <div className="col-span-full mt-4 sm:col-span-3">
                    <CheckboxInput
                      name="sendToClinic"
                      label="Ship to Clinic"
                      required
                      size="lg"
                      description="Select to ship the first order to the clinic."
                    />
                  </div>
                </ReferralsProductionListModalFormInner>
              )}
            </div>
          </>
        ) : (
          <div />
        )}
      </Modal>
    );
  }

  return shouldRender;
}

ReferralsProductionListModalForm.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
  currentRecord: PropTypes.object,
  open: PropTypes.bool.isRequired,
};

export default ReferralsProductionListModalForm;
